import { useMemo, useState } from "react";

import {
  CheckBadgeIcon,
  MagnifyingGlassIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import classNames from "../../../utils/classNames";
import { IPart, IPartFitment } from "../../../models/Part";

const formatYears = (from?: number | null, to?: number | null) => {
  if (from && to) return from === to ? `${from}` : `${from} – ${to}`;
  if (from) return `${from}+`;
  if (to) return `–${to}`;
  return "—";
};

const confidenceTone = (c?: number | null) => {
  if (typeof c !== "number") return "bg-gray-100 text-gray-600 ring-gray-200";
  if (c >= 80) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (c >= 60) return "bg-sky-50 text-sky-700 ring-sky-200";
  if (c >= 40) return "bg-amber-50 text-amber-700 ring-amber-200";
  return "bg-red-50 text-red-700 ring-red-200";
};

export default function StockFitment({ part }: { part: IPart }) {
  const [search, setSearch] = useState("");

  const all = useMemo<IPartFitment[]>(
    () => (Array.isArray(part.fitment) ? part.fitment : []),
    [part.fitment]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter((f) => {
      const haystack = [
        f.make,
        f.model,
        f.trim,
        f.variant,
        f.engine,
        f.transmission,
        f.bodyStyle,
        f.fuelType,
        f.notes,
        f.source,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [all, search]);

  const summary = useMemo(() => {
    const makes = new Set<string>();
    const models = new Set<string>();
    let earliest: number | null = null;
    let latest: number | null = null;
    for (const f of all) {
      if (f.make) makes.add(f.make);
      if (f.make && f.model) models.add(`${f.make} ${f.model}`);
      if (f.yearFrom != null)
        earliest = earliest === null ? f.yearFrom : Math.min(earliest, f.yearFrom);
      if (f.yearTo != null)
        latest = latest === null ? f.yearTo : Math.max(latest, f.yearTo);
    }
    return {
      total: all.length,
      makes: makes.size,
      models: models.size,
      yearRange:
        earliest || latest ? `${earliest ?? "?"} – ${latest ?? "?"}` : "—",
    };
  }, [all]);

  return (
    <div className="flex flex-col gap-4">
      <Card title="Vehicle Fitment" bodyClassName="p-4">
        <p className="text-xs text-gray-500 mb-4">
          Other vehicles this part may fit. Use these as a starting point and
          always double-check against OE part numbers and physical fitment
          before listing.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Total Fitments" value={summary.total} />
          <Stat label="Distinct Makes" value={summary.makes} />
          <Stat label="Distinct Models" value={summary.models} />
          <Stat label="Year Range" value={summary.yearRange} />
        </div>

        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by make, model, engine…"
              className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-sm placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          {search && (
            <span className="text-xs text-gray-500">
              {filtered.length} of {all.length}
            </span>
          )}
        </div>

        {all.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <TruckIcon
              className="mx-auto h-8 w-8 text-gray-300"
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-gray-700">
              No fitment data for this part
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Once the backend exposes vehicle fitments (e.g. derived from
              AutoDap or eBay compatibility lists), they'll appear here.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
            No fitments match "{search}"
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Years</Th>
                  <Th>Make</Th>
                  <Th>Model</Th>
                  <Th>Trim / Variant</Th>
                  <Th>Engine</Th>
                  <Th>Transmission</Th>
                  <Th>Body</Th>
                  <Th>Fuel</Th>
                  <Th className="text-center">Confidence</Th>
                  <Th>Source</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filtered.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50/60">
                    <Td className="font-mono text-xs">
                      {formatYears(f.yearFrom, f.yearTo)}
                    </Td>
                    <Td className="font-medium text-gray-900">
                      {f.make ?? "—"}
                    </Td>
                    <Td className="text-gray-900">{f.model ?? "—"}</Td>
                    <Td>
                      {[f.trim, f.variant].filter(Boolean).join(" ") || "—"}
                    </Td>
                    <Td>{f.engine ?? "—"}</Td>
                    <Td>{f.transmission ?? "—"}</Td>
                    <Td>{f.bodyStyle ?? "—"}</Td>
                    <Td>{f.fuelType ?? "—"}</Td>
                    <Td className="text-center">
                      {typeof f.confidence === "number" ? (
                        <span
                          className={classNames(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                            confidenceTone(f.confidence)
                          )}
                        >
                          {f.confidence >= 80 && (
                            <CheckBadgeIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                          )}
                          {Math.round(f.confidence)}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </Td>
                    <Td className="text-xs text-gray-500">
                      {f.source ?? "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600",
        className
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={classNames("px-3 py-2 text-sm text-gray-700", className)}>
      {children}
    </td>
  );
}
